import {
  Button,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  Input,
  Paragraph,
  ScrollView,
  YStack,
  Textarea,
  Switch,
  Slider,
  AlertDialog,
  Alert,
  AlertTitle,
  AlertDescription,
  AlertButton,
  Tile,
  IconButton,
  TileTitleContainer,
  TileTitle,
  TileTitleButton,
  TileButton,
  TileDescription,
  HomeIcon,
  Stat,
  StatTitle,
  StatNumber,
  Select,
  Sheet,
  ButtonColorScheme,
} from '@bounty/creators-design-system'
import { useState } from 'react'
import { useLink } from 'solito/link'
import { z } from 'zod'
import { useFastForm } from '../../components'
import { Video } from '../../components/Video'
import { logger } from '../../utils/logger'

const SheetDemo = () => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        event="Design System Sheet Open Button Clicked"
        onPress={() => setOpen(true)}
      >
        Open
      </Button>
      <Sheet title="Title" open={open} onOpenChange={setOpen}>
        <Paragraph>hello</Paragraph>
      </Sheet>
    </>
  )
}

const SelectDemo = () => {
  const items = [
    { name: 'Apple', value: 'Apple' },
    { name: 'Pear', value: 'Pear' },
    { name: 'Blackberry', value: 'Blackberry' },
    { name: 'Peach', value: 'Peach' },
    { name: 'Apricot', value: 'Apricot' },
    { name: 'Melon', value: 'Melon' },
    { name: 'Honeydew', value: 'Honeydew' },
    { name: 'Starfruit', value: 'Starfruit' },
    { name: 'Blueberry', value: 'Blueberry' },
    { name: 'Raspberry', value: 'Raspberry' },
    { name: 'Strawberry', value: 'Strawberry' },
    { name: 'Mango', value: 'Mango' },
    { name: 'Pineapple', value: 'Pineapple' },
    { name: 'Lime', value: 'Lime' },
    { name: 'Lemon', value: 'Lemon' },
    { name: 'Coconut', value: 'Coconut' },
    { name: 'Guava', value: 'Guava' },
    { name: 'Papaya', value: 'Papaya' },
    { name: 'Orange', value: 'Orange' },
    { name: 'Grape', value: 'Grape' },
    { name: 'Jackfruit', value: 'Jackfruit' },
    { name: 'Durian', value: 'Durian' },
  ]
  const [val, setVal] = useState('apple')

  return (
    <Select
      name="test"
      items={items}
      itemsLabel={'Fruits'}
      value={val}
      onValueChange={setVal}
    />
  )
}

const ButtonDemo = () => {
  const [colorScheme, setColorScheme] = useState<ButtonColorScheme>('primary')
  const items: { name: string; value: ButtonColorScheme }[] = [
    { name: 'Primary', value: 'primary' },
    { name: 'Neutral', value: 'neutral' },
    { name: 'Warning', value: 'warning' },
    { name: 'Error', value: 'error' },
    { name: 'Success', value: 'success' },
  ]

  return (
    <YStack space="$4">
      <Select<ButtonColorScheme>
        items={items}
        itemsLabel={'Color Schemes'}
        value={colorScheme}
        onValueChange={setColorScheme}
        name="test"
      />
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        size="$xs"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        size="$sm"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        size="$md"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        size="$lg"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="outline"
        size="$xs"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="outline"
        size="$sm"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="outline"
        size="$md"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="outline"
        size="$lg"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="ghost"
        size="$xs"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="ghost"
        size="$sm"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="ghost"
        size="$md"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="ghost"
        size="$lg"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="link"
        size="$xs"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="link"
        size="$sm"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="link"
        size="$md"
      >
        Button
      </Button>
      <Button
        event="Design System Button Clicked"
        colorScheme={colorScheme}
        variant="link"
        size="$lg"
      >
        Button
      </Button>
    </YStack>
  )
}

const AlertDialogDemo = () => {
  return (
    <YStack space="$4">
      <AlertDialog
        acceptEvent={'Design System Alert Event Clicked'}
        trigger={
          <Button event="Design System Dialog Open Button Clicked">
            Trigger Alert
          </Button>
        }
        title="Are you sure?"
        description="You cannot go back after clicking this button!"
        onSubmitPress={() => {
          logger.log('submit pressed!')
        }}
      />
    </YStack>
  )
}

const FastFormDemo = () => {
  const items = [
    { name: 'Apple', value: 'Apple' },
    { name: 'Pear', value: 'Pear' },
    { name: 'Blackberry', value: 'Blackberry' },
    { name: 'Peach', value: 'Peach' },
    { name: 'Apricot', value: 'Apricot' },
    { name: 'Melon', value: 'Melon' },
    { name: 'Honeydew', value: 'Honeydew' },
    { name: 'Starfruit', value: 'Starfruit' },
    { name: 'Blueberry', value: 'Blueberry' },
    { name: 'Raspberry', value: 'Raspberry' },
    { name: 'Strawberry', value: 'Strawberry' },
    { name: 'Mango', value: 'Mango' },
    { name: 'Pineapple', value: 'Pineapple' },
    { name: 'Lime', value: 'Lime' },
    { name: 'Lemon', value: 'Lemon' },
    { name: 'Coconut', value: 'Coconut' },
    { name: 'Guava', value: 'Guava' },
    { name: 'Papaya', value: 'Papaya' },
    { name: 'Orange', value: 'Orange' },
    { name: 'Grape', value: 'Grape' },
    { name: 'Jackfruit', value: 'Jackfruit' },
    { name: 'Durian', value: 'Durian' },
  ]

  const {
    Form,
    Input,
    Textarea,
    SubmitButton,
    Select,
    Switch,
    Slider,
    methods,
  } = useFastForm({
    schema: z.object({
      name: z.string().min(1),
      description: z.string().max(300),
      fruit: z.string().min(1),
      optIntoEmails: z.boolean(),
      rating: z.number().min(0).max(10),
    }),
    // defaultValues: {
    //   name: 'Marcus',
    //   description: 'Cookies and tacos',
    //   fruit: 'Lime',
    // },
  })

  return (
    <YStack space="$4">
      <Form
        onSubmit={(values) => {
          logger.log('on submit called', values)
        }}
      >
        <Input
          name="name"
          label="Name"
          helpText="Put in your name please"
          placeholder="name"
        />
        <Textarea
          name="description"
          label="Description"
          helpText="Tell us about yourself"
          placeholder="I am a..."
        />
        <Select
          name="fruit"
          label="Favorite Fruit"
          items={items}
          itemsLabel="Fruit"
          placeholder="Choose a fruit!"
          helpText="Choose a fruit!"
        />
        <Switch
          name="optIntoEmails"
          label="Opt into emails"
          helpText="We don't email often"
        />
        <Slider
          name="rating"
          label="Rating"
          helpText="Please rate good"
          min={0}
          max={10}
        />
        <SubmitButton event={'Design System Form Button Clicked'}>
          Submit
        </SubmitButton>
      </Form>
    </YStack>
  )
}

export function DesignSystem() {
  const linkProps = useLink({
    href: '/',
  })

  return (
    <ScrollView>
      <YStack space="$8" paddingTop="$10" px="$4">
        <Button event="Design System Go Back Clicked" {...linkProps}>
          Go Back
        </Button>
        <H1>Tile</H1>
        <YStack space="$4">
          <Tile>
            <TileTitleContainer>
              <TileTitle>Title</TileTitle>
              <TileTitleButton event="testing" icon={HomeIcon} />
            </TileTitleContainer>
            <TileDescription>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Libero ab
              suscipit deleniti dolores doloribus vero fugit repellendus a,
              aspernatur sapiente, nulla ipsam, molestias eum possimus esse at
              inventore! Ratione, asperiores.
            </TileDescription>
            <TileButton event="Design System Tile Button Clicked">
              Button
            </TileButton>
          </Tile>
        </YStack>
        <H1>Icon Button</H1>
        <YStack space="$4">
          <IconButton
            event="testing"
            size="$xs"
            alignSelf="flex-start"
            icon={HomeIcon}
          />
          <IconButton
            event="testing"
            size="$sm"
            alignSelf="flex-start"
            icon={HomeIcon}
          />
          <IconButton
            event="testing"
            size="$md"
            alignSelf="flex-start"
            icon={HomeIcon}
          />
          <IconButton
            event="testing"
            size="$lg"
            alignSelf="flex-start"
            icon={HomeIcon}
          />
        </YStack>
        <H1>Alert</H1>
        <YStack space="$4">
          <Alert>
            <AlertTitle>Title</AlertTitle>
            <AlertDescription>
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
              Laudantium, at error. Eligendi maxime ipsa voluptate quis? Nisi
              vero sint, atque, aspernatur optio aperiam quas libero incidunt,
              illum est distinctio porro.
            </AlertDescription>
            <AlertButton event="Design System Alert Button Clicked">
              Button
            </AlertButton>
          </Alert>
          <Alert status="success">
            <AlertTitle>Title</AlertTitle>
            <AlertDescription>
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
              Laudantium, at error. Eligendi maxime ipsa voluptate quis? Nisi
              vero sint, atque, aspernatur optio aperiam quas libero incidunt,
              illum est distinctio porro.
            </AlertDescription>
            <AlertButton event="Design System Alert Button Clicked">
              Button
            </AlertButton>
          </Alert>
          <Alert status="error">
            <AlertTitle>Title</AlertTitle>
            <AlertDescription>
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
              Laudantium, at error. Eligendi maxime ipsa voluptate quis? Nisi
              vero sint, atque, aspernatur optio aperiam quas libero incidunt,
              illum est distinctio porro.
            </AlertDescription>
            <AlertButton event="Design System Alert Button Clicked">
              Button
            </AlertButton>
          </Alert>
          <Alert status="neutral">
            <AlertTitle>Title</AlertTitle>
            <AlertDescription>
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
              Laudantium, at error. Eligendi maxime ipsa voluptate quis? Nisi
              vero sint, atque, aspernatur optio aperiam quas libero incidunt,
              illum est distinctio porro.
            </AlertDescription>
            <AlertButton event="Design System Alert Button Clicked">
              Button
            </AlertButton>
          </Alert>
          <Alert status="warning">
            <AlertTitle>Title</AlertTitle>
            <AlertDescription>
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
              Laudantium, at error. Eligendi maxime ipsa voluptate quis? Nisi
              vero sint, atque, aspernatur optio aperiam quas libero incidunt,
              illum est distinctio porro.
            </AlertDescription>
            <AlertButton event="Design System Alert Button Clicked">
              Button
            </AlertButton>
          </Alert>
        </YStack>
        <YStack space="$4">
          <H1>H1 - Welcome to bounty.</H1>
          <H2>H2 - Welcome to bounty.</H2>
          <H3>H3 - Welcome to bounty.</H3>
          <H4>H4 - Welcome to bounty.</H4>
          <H5>H5 - Welcome to bounty.</H5>
          <H6>H6 - Welcome to bounty.</H6>
        </YStack>
        <H1>Text Regular</H1>
        <YStack space="$4">
          <Paragraph size="$6xl">6xl - Welcome to bounty.</Paragraph>
          <Paragraph size="$5xl">5xl - Welcome to bounty.</Paragraph>
          <Paragraph size="$4xl">4xl - Welcome to bounty.</Paragraph>
          <Paragraph size="$3xl">3xl - Welcome to bounty.</Paragraph>
          <Paragraph size="$2xl">2xl - Welcome to bounty.</Paragraph>
          <Paragraph size="$xl">xl - Welcome to bounty.</Paragraph>
          <Paragraph size="$lg">lg - Welcome to bounty.</Paragraph>
          <Paragraph size="$md">md - Welcome to bounty.</Paragraph>
          <Paragraph size="$sm">sm - Welcome to bounty.</Paragraph>
          <Paragraph size="$xs">xs - Welcome to bounty.</Paragraph>
        </YStack>
        <H1>Text Semibold</H1>
        <YStack space="$4">
          <Paragraph fontWeight="$semibold" size="$6xl">
            6xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$5xl">
            5xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$4xl">
            4xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$3xl">
            3xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$2xl">
            2xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$xl">
            xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$lg">
            lg - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$md">
            md - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$sm">
            sm - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$semibold" size="$xs">
            xs - Welcome to bounty.
          </Paragraph>
        </YStack>
        <H1>Text Bold</H1>
        <YStack space="$4">
          <Paragraph fontWeight="$bold" size="$6xl">
            6xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$5xl">
            5xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$4xl">
            4xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$3xl">
            3xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$2xl">
            2xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$xl">
            xl - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$lg">
            lg - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$md">
            md - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$sm">
            sm - Welcome to bounty.
          </Paragraph>
          <Paragraph fontWeight="$bold" size="$xs">
            xs - Welcome to bounty.
          </Paragraph>
        </YStack>
        <H1>Video</H1>
        <Video
          url={
            'https://res.cloudinary.com/earn-bounty/video/upload/v1640128020/ugc-samples/toothpaste_tn7lop.mp4'
          }
        />
        <H1>Button</H1>
        <ButtonDemo />
        <H1>Input</H1>
        <YStack space="$4">
          <Input placeholder="hello world" />
        </YStack>
        <H1>Textarea</H1>
        <YStack space="$4">
          <Textarea placeholder="hello world" />
        </YStack>
        <H1>Select</H1>
        <YStack space="$4" flexGrow={0}>
          <SelectDemo />
        </YStack>
        <H1>Slider</H1>
        <YStack space="$4">
          <Slider />
        </YStack>
        <H1>Switch</H1>
        <YStack space="$4">
          <Switch label="Enable email?" id="email" />
        </YStack>
        <H1>Fast Form</H1>
        <FastFormDemo />
        <H1>Sheet</H1>
        <YStack space="$4" flexGrow={0}>
          <SheetDemo />
        </YStack>
        <H1>Alert Dialog</H1>
        <AlertDialogDemo />
        <H1>Stat</H1>
        <YStack space="$4">
          <Stat>
            <StatTitle>Current rewards</StatTitle>
            <StatNumber>$48</StatNumber>
          </Stat>
          <Stat colorScheme="success">
            <StatTitle>Current rewards</StatTitle>
            <StatNumber>$48</StatNumber>
          </Stat>
          <Stat colorScheme="warning">
            <StatTitle>Current rewards</StatTitle>
            <StatNumber>$48</StatNumber>
          </Stat>
          <Stat colorScheme="error">
            <StatTitle>Current rewards</StatTitle>
            <StatNumber>$48</StatNumber>
          </Stat>
          <Stat colorScheme="neutral">
            <StatTitle>Current rewards</StatTitle>
            <StatNumber>$48</StatNumber>
          </Stat>
        </YStack>
      </YStack>
    </ScrollView>
  )
}
